import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  LinearProgress,
  useTheme,
} from "@mui/material";
import {
  Psychology as AiIcon,
  AutoAwesome as SparkleIcon,
} from "@mui/icons-material";
import { Theme } from "@mui/material/styles";

// Flat UI Color System - Theme Aware (matching INSIGHT_DESIGN_PRINCIPLES.md)
const getFlatUIColors = (theme: Theme) => {
  const isDark = theme.palette.mode === "dark";

  return {
    // Primary Actions & Growth
    primary: "#22c55e", // Main green for positive actions
    primaryHover: "#16a34a", // Darker green for hover states

    // Neutral Interface (theme-dependent)
    background: isDark ? "#1a1a1a" : "#ffffff", // Dark grey-black or pure white
    surface: isDark ? "#212121" : "#f8fafc", // Slightly lighter dark or subtle gray
    border: isDark ? "rgba(255,255,255,0.1)" : "#e2e8f0", // Subtle white lines or light gray

    // Typography (inverted for dark theme)
    textPrimary: isDark ? "#e4e6ea" : "#0f172a", // Light or dark text
    textSecondary: isDark ? "#9ca3af" : "#64748b", // Medium contrast text
    textMuted: isDark ? "#6b7280" : "#94a3b8", // Low contrast text
  };
};

interface AiGenerationLoaderProps {
  isGenerating: boolean;
}

const AiGenerationLoader: React.FC<AiGenerationLoaderProps> = ({ isGenerating }) => {
  const theme = useTheme();
  const flatUIColors = getFlatUIColors(theme);

  if (!isGenerating) {
    return null;
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 4,
        py: 6,
        px: 4,
        textAlign: "center",
        backgroundColor: flatUIColors.surface,
        border: `1px solid ${flatUIColors.border}`,
        borderRadius: 2,
        maxWidth: 600,
        mx: "auto",
      }}
    >
      {/* AI Icon with subtle animation */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          mb: 2,
        }}
      >
        <AiIcon
          sx={{
            fontSize: 48,
            color: flatUIColors.primary,
            animation: "pulse 2s ease-in-out infinite",
            "@keyframes pulse": {
              "0%, 100%": {
                opacity: 1,
              },
              "50%": {
                opacity: 0.7,
              },
            },
          }}
        />
        <SparkleIcon
          sx={{
            fontSize: 24,
            color: flatUIColors.primary,
            animation: "sparkle 1.5s ease-in-out infinite",
            "@keyframes sparkle": {
              "0%, 100%": {
                transform: "scale(1) rotate(0deg)",
                opacity: 0.8,
              },
              "50%": {
                transform: "scale(1.1) rotate(180deg)",
                opacity: 1,
              },
            },
          }}
        />
      </Box>

      {/* Main heading */}
      <Typography
        variant="h5"
        sx={{
          fontWeight: 600,
          color: flatUIColors.textPrimary,
          mb: 1,
          fontSize: "1.5rem",
          lineHeight: 1.3,
        }}
      >
        Generating AI Earnings Summary
      </Typography>

      {/* Informative message */}
      <Typography
        variant="body1"
        sx={{
          color: flatUIColors.textSecondary,
          lineHeight: 1.6,
          maxWidth: 480,
          mx: "auto",
          mb: 3,
        }}
      >
        We're using state-of-the-art AI models to analyze this earnings call and
        create a summarised transcript. This can take up to a minute.
      </Typography>

      {/* Continuous Loading Bar */}
      <Box sx={{ width: "100%", mb: 2 }}>
        <LinearProgress
          sx={{
            height: 8,
            borderRadius: 1,
            backgroundColor: flatUIColors.border,
            "& .MuiLinearProgress-bar": {
              backgroundColor: flatUIColors.primary,
              borderRadius: 1,
            },
          }}
        />
      </Box>


    </Box>
  );
};

export default AiGenerationLoader;